import React, { useEffect, useState } from 'react';
import { Drawer, Box, Typography, IconButton, Divider, Chip, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Alert, Tooltip } from '@mui/material';
import { Icon } from '@iconify/react';
import { getPasswordResetHistoryByUserId, PasswordResetHistoryEntry } from '../utils/passwordResetHistory';
interface PasswordResetHistoryDrawerProps {
  open: boolean;
  onClose: () => void;
  userId?: string;
  userName?: string;
}
const PasswordResetHistoryDrawer: React.FC<PasswordResetHistoryDrawerProps> = ({
  open,
  onClose,
  userId,
  userName
}) => {
  const [history, setHistory] = useState<PasswordResetHistoryEntry[]>([]);
  useEffect(() => {
    if (open && userId) {
      const userHistory = getPasswordResetHistoryByUserId(userId);
      setHistory(userHistory);
    }
  }, [open, userId]);
  const getResetByColor = (resetBy: string) => {
    switch (resetBy) {
      case 'BO_ADMIN':
        return {
          bg: 'rgba(79, 70, 229, 0.1)',
          color: '#4F46E5'
        };
      case 'SYSTEM':
        return {
          bg: 'rgba(34, 211, 238, 0.1)',
          color: '#22D3EE'
        };
      case 'USER':
        return {
          bg: 'rgba(16, 185, 129, 0.1)',
          color: '#10B981'
        };
      default:
        return {
          bg: 'rgba(148, 163, 184, 0.1)',
          color: '#94A3B8'
        };
    }
  };
  const getDeliveryStatusColor = (status: string) => {
    switch (status) {
      case 'SUCCESS':
        return {
          bg: 'rgba(16, 185, 129, 0.1)',
          color: '#10B981'
        };
      case 'FAILED':
        return {
          bg: 'rgba(239, 68, 68, 0.1)',
          color: '#EF4444'
        };
      case 'PENDING':
        return {
          bg: 'rgba(245, 158, 11, 0.1)',
          color: '#F59E0B'
        };
      default:
        return {
          bg: 'rgba(148, 163, 184, 0.1)',
          color: '#94A3B8'
        };
    }
  };
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };
  return <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{
    sx: {
      width: {
        xs: '100%',
        sm: 600,
        md: 700
      },
      p: 0
    }
  }} data-id="element-1377">
      <Box sx={{
      p: 3,
      borderBottom: '1px solid #E5E7EB'
    }} data-id="element-1378">
        <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }} data-id="element-1379">
          <Box data-id="element-1380">
            <Typography variant="h6" fontWeight={600} data-id="element-1381">
              Password Reset History
            </Typography>
            {userName && <Typography variant="body2" color="text.secondary" mt={0.5} data-id="element-1382">
                User: {userName}
              </Typography>}
          </Box>
          <IconButton onClick={onClose} size="small" data-id="element-1383">
            <Icon icon="lucide:x" width={18} height={18} data-id="element-1384" />
          </IconButton>
        </Box>
      </Box>
      <Box sx={{
      p: 3,
      overflowY: 'auto',
      height: 'calc(100vh - 100px)'
    }} data-id="element-1385">
        {history.length === 0 ? <Alert severity="info" data-id="element-1386">
            No password reset history found for this user.
          </Alert> : <TableContainer component={Paper} elevation={0} data-id="element-1387">
            <Table size="small" data-id="element-1388">
              <TableHead data-id="element-1389">
                <TableRow data-id="element-1390">
                  <TableCell data-id="element-1391">
                    <Typography variant="subtitle2" fontWeight={600} data-id="element-1392">
                      Timestamp
                    </Typography>
                  </TableCell>
                  <TableCell data-id="element-1393">
                    <Typography variant="subtitle2" fontWeight={600} data-id="element-1394">
                      Reset By
                    </Typography>
                  </TableCell>
                  <TableCell data-id="element-1395">
                    <Typography variant="subtitle2" fontWeight={600} data-id="element-1396">
                      Delivery Status
                    </Typography>
                  </TableCell>
                  <TableCell data-id="element-1397">
                    <Typography variant="subtitle2" fontWeight={600} data-id="element-1398">
                      Details
                    </Typography>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody data-id="element-1399">
                {history.map(entry => {
              const resetByStyle = getResetByColor(entry.resetBy);
              const deliveryStyle = getDeliveryStatusColor(entry.deliveryStatus);
              return <TableRow key={entry.id} data-id="element-1400">
                      <TableCell data-id="element-1401">
                        <Typography variant="body2" fontSize="0.8rem" data-id="element-1402">
                          {formatDate(entry.timestamp)}
                        </Typography>
                      </TableCell>
                      <TableCell data-id="element-1403">
                        <Chip label={entry.resetBy.replace('_', ' ')} size="small" sx={{
                    backgroundColor: resetByStyle.bg,
                    color: resetByStyle.color,
                    fontWeight: 600,
                    fontSize: '0.7rem'
                  }} data-id="element-1404" />
                        {entry.resetByName && <Typography variant="caption" display="block" color="text.secondary" mt={0.5} data-id="element-1405">
                            {entry.resetByName}
                          </Typography>}
                      </TableCell>
                      <TableCell data-id="element-1406">
                        <Tooltip title={entry.deliveryError || entry.emailMessageId || ''} arrow data-id="element-1407">
                          <Chip label={entry.deliveryStatus} size="small" sx={{
                      backgroundColor: deliveryStyle.bg,
                      color: deliveryStyle.color,
                      fontWeight: 600,
                      fontSize: '0.7rem'
                    }} data-id="element-1408" />
                        </Tooltip>
                        {entry.deliveryStatus === 'SUCCESS' && entry.emailMessageId && <Typography variant="caption" display="block" color="text.secondary" mt={0.5} data-id="element-1409">
                              ID: {entry.emailMessageId.substring(0, 16)}...
                            </Typography>}
                      </TableCell>
                      <TableCell data-id="element-1410">
                        {entry.reason && <Typography variant="body2" fontSize="0.8rem" mb={0.5} data-id="element-1411">
                            <strong data-id="element-1412">Reason:</strong> {entry.reason}
                          </Typography>}
                        <Typography variant="body2" fontSize="0.8rem" color="text.secondary" data-id="element-1413">
                          Email: {entry.recipientEmail}
                        </Typography>
                        {entry.deliveryError && <Typography variant="body2" fontSize="0.8rem" color="error" mt={0.5} data-id="element-1414">
                            <Icon icon="lucide:alert-circle" width={12} height={12} style={{
                      marginRight: 4
                    }} data-id="element-1415" />
                            {entry.deliveryError}
                          </Typography>}
                      </TableCell>
                    </TableRow>;
            })}
              </TableBody>
            </Table>
          </TableContainer>}
      </Box>
    </Drawer>;
};
export default PasswordResetHistoryDrawer;